/**
 * Factory for the dropdown menu
 */
import React from 'react';
import MediaQuery from 'react-responsive';

import { BREAKPOINT_SIZES, BREAKPOINTS } from 'tradera-constants/breakpoints';
import {
    useTranslator,
    useUrlLocalizer,
    useCurrentLanguage
} from 'tradera-lang/translate';
import { filterCategories } from 'tradera-helpers/filter-categories-helper';
import MenuItemSeparator from 'tradera-components/site-header/dropdown-menu-item-separator';
import MenuItemButton from 'tradera-components/site-header/dropdown-menu-item-button';
import MenuItem from 'tradera-components/site-header/dropdown-menu-item';
import ExpandableMenuItem from 'tradera-components/site-header/dropdown-menu-item-expandable';
import ALink from 'tradera-components/alink/alink';
import CategoryIcon from '@tradera/blueprint/theme/icons2/category.svg?jsx';
import EndingSoonIcon from '@tradera/blueprint/theme/icons2/branded-ending.svg?jsx';
import InfoIcon from '@tradera/blueprint/theme/icons2/info.svg?jsx';
import PenIcon from '@tradera/blueprint/theme/icons2/pen.svg?jsx';
import RecommendedIcon from '@tradera/blueprint/theme/icons2/branded-recommended.svg?jsx';
import SearchIcon from '@tradera/blueprint/theme/icons2/search.svg?jsx';
import BrandIcon from '@tradera/blueprint/theme/icons2/sale.svg?jsx';
import UserIcon from '@tradera/blueprint/theme/icons2/user.svg?jsx';
import CategoryCharityIcon from '@tradera/blueprint/theme/icons2/category-charity.svg?jsx';
import ValuationIcon from '@tradera/blueprint/theme/icons2/valuation.svg?jsx';
import InspirationIcon from '@tradera/blueprint/theme/icons2/inspiration.svg?jsx';
import { getCategoryIcon } from 'tradera-helpers/category-icon-helper';
import { useMenuHeightFix } from 'tradera-hooks/use-menu-height-fix';
import { useIsFeatureEnabled } from 'tradera-hooks/use-is-feature-enabled';
import { isSwedishOrUndefined } from 'tradera-utils/languagecode';

const getGaAttributes = label => ({
    'data-ga-category': 'Top navigation',
    'data-ga-action': 'Category menu',
    'data-ga-label': `Link - ${label}`
});

const DropdownMenuContent = ({ categories, inSPA, isLoggedIn, onClick }) => {
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    const isNextWebBrandsPageEnabled = useIsFeatureEnabled(
        'next-web-brands-page'
    );
    const isPennybridgeCharitiesEnabled = useIsFeatureEnabled(
        'pennybridge-charities'
    );
    const isValuationPageEnabled = useIsFeatureEnabled('valuation-page');
    const mediaProps = { maxWidth: BREAKPOINT_SIZES[BREAKPOINTS.MD].max };

    const sourceCategories =
        categories.list.length > 0
            ? categories.list
            : categories.topLevelCategories;
    // Only show rebate codes for non logged in users.
    const categoryList = sourceCategories
        .map(category => ({ ...category, open: false }))
        .filter(category => !isLoggedIn || (isLoggedIn && category.id !== 0));

    const menuContentRef = useMenuHeightFix();
    const { language } = useCurrentLanguage();

    const isSwedish = isSwedishOrUndefined(language);

    return (
        <ul className="plain-list" ref={menuContentRef}>
            {/* NOTE: Notifications are disabled for burger button for desktop due to hidden cart and messaging, see DropDownMenuHamburger */}
            <MediaQuery {...mediaProps}>
                {!isLoggedIn && (
                    <>
                        <ExpandableMenuItem
                            label={t('siteHeader_howTo_v2')}
                            className="site-dropdown__item-with-icon text-inter-medium"
                            icon={<InfoIcon className="icon icon-md" />}
                            border>
                            <MenuItem
                                className="pl-7 text-inter-medium site-dropdown__item-default"
                                label={t('siteHeader_howToBuy_v2')}
                                link={toLocalizedUrl('/how-to-buy')}
                                gaAttributes={getGaAttributes('How to buy')}
                            />
                            <MenuItem
                                className="pl-7 text-inter-medium site-dropdown__item-default"
                                label={t('siteHeader_howToSell_v2')}
                                link={toLocalizedUrl('/how-to-sell')}
                                gaAttributes={getGaAttributes('How to sell')}
                            />
                            {isSwedish && (
                                <MenuItem
                                    className="pl-7 text-inter-medium site-dropdown__item-default"
                                    label={t('siteHeader_howToSellCompany')}
                                    link={'https://info.tradera.com/foretag/'}
                                    gaAttributes={getGaAttributes(
                                        'How to sell company'
                                    )}
                                />
                            )}
                        </ExpandableMenuItem>
                        <MenuItem
                            border
                            className="site-dropdown__item-with-icon"
                            gaAttributes={getGaAttributes('Signup')}
                            icon={<UserIcon className="icon icon-md" />}
                            inSPA={false}
                            label={t('siteHeader_register')}
                            link="/register"
                            onClick={onClick}
                        />
                        <MenuItem
                            border
                            className="site-dropdown__item-with-icon"
                            gaAttributes={getGaAttributes('Create new listing')}
                            icon={<PenIcon className="icon icon-md" />}
                            inSPA={false}
                            label={t('siteHeader_createItem')}
                            link={toLocalizedUrl('/selling')}
                            onClick={onClick}
                        />
                    </>
                )}
            </MediaQuery>

            <MenuItem
                border
                className="site-dropdown__item-with-icon"
                follow={false}
                gaAttributes={getGaAttributes('Trending')}
                icon={<RecommendedIcon className="icon icon-md" />}
                inSPA={false}
                label={
                    isLoggedIn
                        ? t('siteHeader_recommendedForYou')
                        : t('siteHeader_recommendedAnonymous')
                }
                link={toLocalizedUrl('/ma/endless-recommendation/selected')}
                onClick={onClick}
            />
            <MenuItem
                border
                className="site-dropdown__item-with-icon"
                gaAttributes={getGaAttributes('Ending soon')}
                icon={<EndingSoonIcon className="icon icon-md" />}
                inSPA={inSPA}
                label={t('siteHeader_lastChance')}
                link={toLocalizedUrl('/campaign/sista-chansen?sortBy=TimeLeft')}
                onClick={onClick}
            />
            <MenuItem
                border
                className="site-dropdown__item-with-icon"
                gaAttributes={getGaAttributes('Brands')}
                icon={<BrandIcon className="icon icon-md" />}
                inSPA={!isNextWebBrandsPageEnabled}
                label={t('siteHeader_brands')}
                link={toLocalizedUrl(
                    `/brands${isNextWebBrandsPageEnabled ? '?next=1' : ''}`
                )}
                onClick={onClick}
            />
            {isPennybridgeCharitiesEnabled && (
                <MenuItem
                    border
                    className="site-dropdown__item-with-icon"
                    gaAttributes={getGaAttributes('Donate')}
                    icon={<CategoryCharityIcon className="icon icon-md" />}
                    inSPA={inSPA}
                    label={t('siteHeader_donateWithTradera')}
                    link={toLocalizedUrl('/causes')}
                    onClick={onClick}
                />
            )}
            {isValuationPageEnabled && (
                <MenuItem
                    border
                    className="site-dropdown__item-with-icon"
                    gaAttributes={getGaAttributes('Priskollen')}
                    icon={<ValuationIcon className="icon icon-md" />}
                    inSPA={inSPA}
                    label={t('siteHeader_valuation')}
                    link={toLocalizedUrl('/valuation')}
                    onClick={onClick}
                />
            )}

            <MenuItemSeparator
                label={
                    <span className="d-block pt-2 bg-white">
                        <CategoryIcon className="icon icon-dark align-text-bottom" />
                        <ALink
                            useLink={inSPA}
                            href={toLocalizedUrl('/kategorier')}
                            {...getGaAttributes('Alla kategorier From top')}
                            className="text-dark pl-2"
                            onClick={onClick}>
                            {t('siteHeader_categoriesAll')}
                        </ALink>
                    </span>
                }
            />
            <CategoryMenuItems
                categoryList={categoryList}
                inSPA={inSPA}
                searchPlaceholder={t('siteHeader_searchAmongCategories')}
                onClick={onClick}
            />
            {!isLoggedIn && (
                <MenuItem
                    border
                    className="site-dropdown__item-with-icon"
                    follow={true}
                    gaAttributes={getGaAttributes('Inspiration')}
                    icon={<InspirationIcon className="icon icon-md" />}
                    inSPA={false}
                    label={t('siteHeader_inspiration')}
                    link={toLocalizedUrl('/inspiration/')}
                    onClick={onClick}
                />
            )}
            {/* NOTE: Fix for iPhone menu making bottom element unclickable */}
            <MediaQuery {...mediaProps}>
                <MenuItemSeparator className="site-dropdown__separator--bottom" />
            </MediaQuery>
        </ul>
    );
};

class CategoryMenuItems extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            filter: '',
            filteredCategories: props.categoryList
        };
    }

    handleFilterChange = event => {
        this.filterCategories(event.target.value.trim());
    };

    handleFilterKey = event => {
        const { key, target } = event;
        switch (key) {
            case 'Escape':
                event.stopPropagation();
                if (this.state.filter === '') {
                    event.target.blur();
                }
                this.filterCategories('');
                break;
            case 'Enter':
                event.stopPropagation();
                target.blur();
                break;
        }
    };

    filterCategories = filter => {
        this.setState({
            filter,
            filteredCategories: filterCategories(
                this.props.categoryList,
                filter
            )
        });
    };

    componentDidUpdate(prevProps) {
        if (
            this.props.categoryList[0]?.categoryNodes !==
            prevProps.categoryList[0]?.categoryNodes
        ) {
            this.filterCategories(this.state.filter);
        }
    }

    render() {
        const { inSPA, searchPlaceholder, onClick } = this.props;
        const { filter, filteredCategories } = this.state;
        return (
            <>
                <MenuItemSeparator
                    label={
                        <div className="col-12 offset-0 col-lg-auto mt-1 mb-2 px-lg-0 mx-lg-2 px-2">
                            <div className="form-control-with-icon">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder={searchPlaceholder}
                                    value={filter}
                                    onKeyDown={this.handleFilterKey}
                                    onChange={this.handleFilterChange}
                                />
                                <SearchIcon className="icon" />
                            </div>
                        </div>
                    }
                    className="bg-white"
                />
                {filteredCategories.map(
                    category =>
                        category && (
                            <CategoryMenuItem
                                key={category.id}
                                category={category}
                                inSPA={category.id !== 0 && inSPA}
                                onClick={onClick}
                            />
                        )
                )}
            </>
        );
    }
}

const CategoryMenuItem = ({
    category: { id, title, categoryNodes, open, href },
    inSPA = false,
    onClick
}) => {
    if (!categoryNodes) {
        return (
            <MenuItem
                className="site-dropdown__item-with-image text-inter-medium"
                link={href}
                imageContainerClassName="bg-light mr-1"
                label={title}
                icon={getCategoryIcon(id, title)}
                border
            />
        );
    }
    return (
        <ExpandableMenuItem
            defaultExpanded={open}
            className="site-dropdown__item-with-image text-inter-medium"
            href={href}
            imageContainerClassName="bg-light mr-1"
            label={title}
            icon={getCategoryIcon(id, title)}
            border>
            {categoryNodes.map(categoryNode => {
                if (categoryNode.isTopLevel) {
                    return (
                        <MenuItemButton
                            key={categoryNode.href}
                            className="mt-4 mb-2 mw-100"
                            containerClass="px-2"
                            gaAttributes={getGaAttributes(
                                categoryNode.isTopLevel
                                    ? title
                                    : `${title} > ${categoryNode.title}`
                            )}
                            inSPA={inSPA}
                            label={categoryNode.title}
                            link={categoryNode.href}
                            onClick={onClick}
                        />
                    );
                }

                return (
                    <MenuItem
                        key={categoryNode.href}
                        bold={categoryNode.isTopLevel}
                        className="site-dropdown__item-default pl-5"
                        gaAttributes={getGaAttributes(
                            categoryNode.isTopLevel
                                ? title
                                : `${title} > ${categoryNode.title}`
                        )}
                        inSPA={inSPA}
                        label={categoryNode.title}
                        link={categoryNode.href}
                        onClick={onClick}
                    />
                );
            })}
        </ExpandableMenuItem>
    );
};

export default DropdownMenuContent;
